import { Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { GetAdminV1OrgMediaSort, MediaKind, type Media } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useMediaList } from '../hooks/use-media-list';
import {
  ANY_DATE,
  isDateFiltered,
  mediaDateParams,
  type MediaDateFilterValue,
} from '../schemas/media-date-filter';
import { UNFILED } from '../schemas/media-list-search';

import { MediaDateFilter } from './media-date-filter';
import { MediaFolderRail } from './media-folder-rail';
import { MediaFolderSelect } from './media-folder-select';
import { MediaGrid } from './media-grid';

const ALL = '__all__';
const PAGE_SIZE = 48;
const KINDS = Object.values(MediaKind);
const SORTS = Object.values(GetAdminV1OrgMediaSort);

const isKind = (v: string): v is MediaKind => (KINDS as string[]).includes(v);
const isSort = (v: string): v is GetAdminV1OrgMediaSort => (SORTS as string[]).includes(v);

export interface MediaPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Already attached; hidden from the grid so they cannot be added twice. */
  excludeIds?: ReadonlySet<string>;
  /** Already attached; shown marked and not pickable, so the grid still says what is there. */
  attachedIds?: ReadonlySet<string>;
  /** What an attached file says. Default "Already added". */
  attachedLabel?: string;
  /** Kind to list; null lists every kind and lets the user narrow it. Images by default. */
  kind?: MediaKind | null;
  /** false picks exactly one file: a click replaces the choice. Several by default. */
  multiple?: boolean;
  onPick: (media: Media[]) => void;
}

/**
 * Choose existing library files. Filters and the folder sit above and beside a grid that scrolls
 * on its own, so Cancel and Add stay in view however long the library is.
 */
export function MediaPickerDialog({ open, onOpenChange, ...rest }: MediaPickerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(88vh,820px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
        {/* Mounted only while open: every opening starts with fresh filters and no selection,
            and nothing is fetched for a picker that is never opened. */}
        <PickerBody onClose={() => onOpenChange(false)} {...rest} />
      </DialogContent>
    </Dialog>
  );
}

interface PickerBodyProps extends Omit<MediaPickerDialogProps, 'open' | 'onOpenChange'> {
  onClose: () => void;
}

function PickerBody({
  onClose,
  excludeIds,
  attachedIds,
  attachedLabel,
  kind = 'image',
  multiple = true,
  onPick,
}: PickerBodyProps) {
  const { t } = useTranslation(['media', 'common']);
  const [selected, setSelected] = useState<Map<string, Media>>(new Map());
  const [folder, setFolder] = useState<string | undefined>(undefined);
  const [kindFilter, setKindFilter] = useState<MediaKind | undefined>(undefined);
  const [date, setDate] = useState<MediaDateFilterValue>(ANY_DATE);
  const [sort, setSort] = useState<GetAdminV1OrgMediaSort>('newest');
  const [text, setText] = useState('');
  const typed = useDebouncedValue(text.trim(), 300);
  // Typing waits for a pause; clearing the box applies at once.
  const q = text.trim() === '' ? '' : typed;

  const listKind = kind ?? kindFilter;
  const query = useMediaList({
    limit: PAGE_SIZE,
    ...(listKind ? { kind: listKind } : {}),
    ...(q ? { q } : {}),
    ...(folder === UNFILED ? { unfiled: 'true' as const } : folder ? { folderId: folder } : {}),
    ...mediaDateParams(date),
    ...(sort === 'newest' ? {} : { sort }),
  });
  const loaded = query.data?.pages.flatMap((p) => p.items);
  const items = loaded?.filter((m) => !excludeIds?.has(m.id));
  const narrowed = Boolean(q) || kindFilter !== undefined || isDateFiltered(date);

  const toggle = (media: Media) =>
    setSelected((prev) => {
      if (!multiple) return prev.has(media.id) ? new Map() : new Map([[media.id, media]]);
      const next = new Map(prev);
      if (next.has(media.id)) next.delete(media.id);
      else next.set(media.id, media);
      return next;
    });

  const clearFilters = () => {
    setText('');
    setKindFilter(undefined);
    setDate(ANY_DATE);
  };

  const confirm = () => {
    onPick([...selected.values()]);
    onClose();
  };

  const loadMore = query.hasNextPage ? (
    <div className="flex justify-center">
      <Button
        variant="outline"
        onClick={() => void query.fetchNextPage()}
        disabled={query.isFetchingNextPage}
      >
        {query.isFetchingNextPage ? t('media:loadingMore') : t('media:loadMore')}
      </Button>
    </div>
  ) : null;

  const message = (copy: string, action?: ReactNode) => (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="text-muted-foreground text-sm">{copy}</p>
      {action}
      {loadMore}
    </div>
  );

  let body: ReactNode;
  if (!query.isError && items?.length === 0) {
    if (loaded && loaded.length > 0) body = message(t('media:picker.allAdded'));
    else if (narrowed)
      body = message(
        t('media:filters.noMatches'),
        <Button variant="outline" size="sm" onClick={clearFilters}>
          {t('media:filters.clear')}
        </Button>,
      );
    else if (folder === UNFILED) body = message(t('media:folders.unfiledEmpty'));
    else if (folder) body = message(t('media:picker.emptyFolder'));
  }
  body ??= (
    <MediaGrid
      items={items}
      isLoading={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      onSelect={toggle}
      selectedIds={new Set(selected.keys())}
      lockedIds={attachedIds}
      lockedLabel={attachedLabel ?? t('media:picker.attached')}
      hasMore={query.hasNextPage}
      isFetchingMore={query.isFetchingNextPage}
      onLoadMore={() => void query.fetchNextPage()}
      columnsClassName="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
    />
  );

  const [only] = selected.values();

  return (
    <>
      <DialogHeader className="border-b px-6 pt-6 pr-12 pb-4">
        <DialogTitle>
          {multiple ? t('media:picker.title') : t('media:picker.chooseTitle')}
        </DialogTitle>
        <DialogDescription>{t('media:picker.description')}</DialogDescription>
      </DialogHeader>

      <div
        className="flex flex-wrap items-center gap-2 border-b px-6 py-3"
        role="search"
        aria-label={t('media:picker.filters')}
      >
        <div className="relative w-full sm:w-60">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            type="search"
            value={text}
            maxLength={120}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('media:filters.searchPlaceholder')}
            aria-label={t('media:filters.search')}
            className="pl-8"
          />
        </div>
        {/* Below md the folder rail is hidden; the same choice lives in a dropdown. */}
        <div className="md:hidden">
          <MediaFolderSelect
            value={folder}
            onChange={setFolder}
            includeUnfiled
            className="w-[180px]"
          />
        </div>
        {kind === null ? (
          <Select
            value={kindFilter ?? ALL}
            onValueChange={(value) => setKindFilter(isKind(value) ? value : undefined)}
          >
            <SelectTrigger className="w-[140px]" aria-label={t('media:filters.kind')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('media:filters.allKinds')}</SelectItem>
              {KINDS.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`media:kind.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <MediaDateFilter value={date} onChange={setDate} />
        <Select value={sort} onValueChange={(value) => isSort(value) && setSort(value)}>
          <SelectTrigger className="w-[160px]" aria-label={t('media:filters.sort')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`media:sort.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {narrowed ? (
          <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
            {t('media:filters.clear')}
          </Button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1">
        <MediaFolderRail
          value={folder}
          onChange={setFolder}
          className="hidden w-56 shrink-0 overflow-y-auto border-r md:flex"
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4" data-testid="media-picker-scroll">
          {body}
        </div>
      </div>

      <DialogFooter className="bg-background items-center border-t px-6 py-3 sm:justify-between">
        <p className="text-muted-foreground min-w-0 truncate text-sm" aria-live="polite">
          {selected.size === 0
            ? multiple
              ? t('media:picker.noneSelected')
              : t('media:picker.noneChosen')
            : multiple
              ? t('media:picker.selected', { count: selected.size })
              : t('media:picker.chosen', {
                  name: only?.filename ?? only?.alt ?? t('media:untitled'),
                })}
          {selected.size > 0 ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="ml-2 h-auto px-0"
              onClick={() => setSelected(new Map())}
            >
              {t('media:picker.clearSelection')}
            </Button>
          ) : null}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" disabled={selected.size === 0} onClick={confirm}>
            {multiple ? t('media:picker.add', { count: selected.size }) : t('media:picker.choose')}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
